import { MagicSignStatus, MagicSignPillTone, SignType } from './types';

// Re-export for callers that previously imported from constants.ts
export { deriveSigningLinks, buildUniqueUploadFileName, isReadyToGenerateLinks } from './utils';
// Shared primitives live in the leaf module (see signTypeConfig.ts for why) — re-exported here so
// existing callers can keep importing them from constants.ts.
export { SIGN_TYPE_LABELS, MAX_SIGNATORIES_DEFAULT } from './signTypeConfig';
import { SIGN_TYPE_LABELS } from './signTypeConfig';

export const MAGIC_SIGN_ENDPOINTS = {
    LIST: '/magic/sign/list',
    // Consolidated: replaces /config and /associateCompany
    UPDATE: '/magic/sign/update',
    GENERATE_LINKS: '/magic/sign/generateLinks',
    CANCEL: '/magic/sign/cancel',
    AUDIT: '/magic/sign/audit',
    DOWNLOAD_DOC: '/magic/sign/download-doc',
    CONFIG_META: '/magic/sign/config-meta',
    // Phase 2 endpoints — /users now handles both company and PNO users via optional orgId param
    USERS: '/magic/sign/users',
    // Consolidated: replaces /sendEmail and /sendReminder
    SEND_NOTIFICATION: '/magic/sign/sendNotification',
    // Phase 3 endpoints
    GLOBAL_LIST: '/magic/sign/global/list',
    /**
     * P2 consolidated intake endpoint (RECUR-26495).
     * Replaces both /upload and /sendForSigning.
     * Body: { mnemosyneDocId, fileName?, source? }
     * Doc must already be registered in DocStore via the MU pipeline.
     */
    CREATE: '/magic/sign/create',
} as const;

export const MAGIC_SIGN_PAGE_SIZE = 20;

/** Section header for the PNO's internal team in the signatory picker. */
export const MAGIC_SIGN_INTERNAL_TEAM_LABEL = 'Internal Team';

/** v1 policy: backend only accepts PDF. DOCX rejected server-side. */
export const MAGIC_SIGN_ALLOWED_TYPES = ['application/pdf'];
export const MAGIC_SIGN_ALLOWED_EXTENSIONS = ['.pdf'];
export const MAGIC_SIGN_PDF_ONLY_TOAST = 'Only PDF documents are supported for signing.';
export const MAGIC_SIGN_MAX_FILE_SIZE_MB = 10;

/** Shared so MagicSignTable, MagicSignFilterPills and GlobalMagicSign never drift into a plain
 *  string alias. */
export const MAGIC_SIGN_STATUS_FILTER_KEY = 'statuses' as const;

/**
 * The Status column, the audit timeline (resolveAuditEventPillTone) and the filter chips all read
 * from here so they can't drift apart. Each `pill` is a class exported by statusPill.module.scss —
 * colour hex lives only there now, never in TypeScript.
 */
export const MAGIC_SIGN_STATUS_CONFIG: Record<MagicSignStatus, { label: string; pill: MagicSignPillTone }> = {
    [MagicSignStatus.UPLOADING]: { label: 'Uploading', pill: 'Neutral' },
    [MagicSignStatus.UPLOADED]: { label: 'Uploaded', pill: 'Grey' },
    [MagicSignStatus.GENERATING_LINKS]: { label: 'Generating Links', pill: 'Neutral' },
    [MagicSignStatus.GENERATED_LINKS]: { label: 'Generated Links', pill: 'Blue' },
    [MagicSignStatus.PARTIAL_SIGNED]: { label: 'Partial Signed', pill: 'LightGreen' },
    [MagicSignStatus.ALL_SIGNED]: { label: 'All Signed', pill: 'Green' },
    [MagicSignStatus.CANCELLED]: { label: 'Cancelled', pill: 'Red' },
} as const;

/**
 * The two user-facing sign-type choices. Aadhaar and DSC are merged into one "Aadhaar/DSC"
 * option that maps to AADHAAR internally (the Leegality flow handles both).
 */
export const SIGN_TYPE_OPTIONS: { id: SignType; label: string }[] = [
    { id: SignType.E_SIGN, label: SIGN_TYPE_LABELS[SignType.E_SIGN] },
    { id: SignType.AADHAAR, label: SIGN_TYPE_LABELS[SignType.AADHAAR] },
];

export const OTP_COOLDOWN_SECONDS = 60;
export const OTP_MAX_ATTEMPTS = 3;

/**
 * Mnemosyne upload flow ID for Magic Sign documents.
 * Value 16 is the platform-wide "general document upload" flow — same value
 * used by Magic Upload (useMnemosyneUpload.ts) and ComposeEmailModal.
 */
export const MAGIC_SIGN_UPLOAD_FLOW_ID = 16;

/**
 * Shared `source` discriminant stamped on MU-registration (/magic/update/docMetaData) and
 * Magic Sign create (/magic/sign/create) calls, for audit/tracing. One definition imported at
 * every call site (FileExplorer, MagicUploadFilesTable, useMagicSignLogic, useGlobalMagicSignLogic)
 * so a typo in one site can't silently mis-tag the source.
 */
export enum MagicSignSource {
    DATA_ROOM = 'DATA_ROOM',
    MAGIC_UPLOAD = 'MAGIC_UPLOAD',
    MAGIC_SIGN = 'MAGIC_SIGN',
}

/**
 * Audit-timeline pill tone. Matching is on substrings of the display string (no type
 * discriminator to switch on instead), so case order is load-bearing.
 */
export function resolveAuditEventPillTone(event: string): MagicSignPillTone {
    const e = (event || '').toLowerCase();
    switch (true) {
        case e.includes('cancel'):
        case e.includes('fail'):
            return 'Red';
        case e.includes('completed'):
        case e.includes('signed'):
            return 'Green';
        // 'signing' doesn't contain 'signed', so this never shadows the case above.
        case e.includes('sent for signing'):
        case e.includes('reminder'):
        case e.includes('opened'):
        case e.includes('otp'):
            return 'Blue';
        case e.includes('upload'):
            return 'Grey';
        // Renders the same grey as Uploaded on purpose, rather than inventing a new hue.
        default:
            return 'Grey';
    }
}
